import {FC, useState, useEffect, useCallback} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Input} from '../../../components/Input/Input'
import {Button} from '../../../components/Button/Button'
import {useNavigate} from 'react-router-dom'
import {GET, POST, DELETE, PUT} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import Swal from 'sweetalert2'
import {i18n} from '../../../translate/i18n'
import SVG from 'react-inlinesvg'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {EditorQuill} from '../../../components/EditorQuill/EditorQuill'
import {SVGUploadModal} from './components/SVGUploadModal'
import {IconEditModal} from './components/IconEditModal'
import useAuth from '../../../hooks/useAuth'
import {typeUser} from '../Configuracoes/Usuarios/typeUser'

type Icon = {
  id: number
  svg: string
  description: string
  category: string
}

const PageContent: FC = () => {
  const navigate = useNavigate()
  const {verifyAdm, verifyCommonAdmin} = useAuth()

  // Função para verificar se o usuário é admin
  const isUserAdmin = () => {
    // Verificamos se o usuário tem perfil de admin E é administrador do plugin
    return verifyCommonAdmin() && verifyAdm()
  }

  const [loading, setLoading] = useState(false)
  const [serviceClassifications, setServiceClassifications] = useState<any[]>([])
  const [serviceClassificationId, setServiceClassificationId] = useState<{
    select: string
    value: number
  }>({select: i18n.t('service_create.form.classification_placeholder'), value: 0})

  const [name, setName] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [time, setTime] = useState<string>('')
  const [ramal, setRamal] = useState<string>('')
  const [externalLink, setExternalLink] = useState<string>('')
  const [status, setStatus] = useState<boolean>(true)
  const [order, setOrder] = useState<number>(0)
  const [iconId, setIconId] = useState<number>(0)
  const [priority, setPriority] = useState<string>('normal')
  const [role, setRole] = useState<string>('')
  const [visibleTo, setVisibleTo] = useState<string>('both') // visível para: employees, customers, both

  //icones
  const [icones, setIcones] = useState<Icon[]>([])
  const [iconesFiltered, setIconesFiltered] = useState<Icon[]>([])
  const [categoryIcons, setCategoryIcons] = useState<any[]>([
    {
      select: i18n.t('service_create.icons.all_categories'),
      value: -1,
    },
  ])
  const [categoryIconsSelect, setCategoryIconsSelect] = useState<any>(-1)
  const [icon, setIcon] = useState<string>('')
  const [loadingIcons, setLoadingIcons] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [searchIconText, setSearchIconText] = useState('')
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingIcon, setEditingIcon] = useState<Icon | null>(null)

  // Estados para seleção múltipla
  const [selectedIcons, setSelectedIcons] = useState<number[]>([])
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false)

  const changeFilterIcons = useCallback(() => {
    setLoadingIcons(true)

    const filtered = icones.filter((icon) => {
      let categoryMatch = true
      let searchMatch = true

      // Filtro por categoria
      if (parseInt(categoryIconsSelect) !== -1) {
        categoryMatch = icon.category === categoryIconsSelect
      }

      // Filtro por texto de busca
      if (searchIconText.trim()) {
        const searchLower = searchIconText.toLowerCase()
        searchMatch = icon.description?.toLowerCase().includes(searchLower)
      }

      return categoryMatch && searchMatch
    })

    setIconesFiltered(filtered)

    setTimeout(() => {
      setLoadingIcons(false)
    }, 300)
  }, [icones, categoryIconsSelect, searchIconText])

  const handleIconUploaded = async (newIcon: Icon) => {
    try {
      // Fecha o modal primeiro
      setShowUploadModal(false)

      // Recarrega a lista completa de ícones do backend para garantir sincronia
      setLoadingIcons(true)
      await getIcones()
      setLoadingIcons(false)

      // Seleciona automaticamente o novo ícone
      setIconId(newIcon.id)
      setIcon(newIcon.svg)

      // Feedback de sucesso
      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Ícone carregado e lista atualizada automaticamente.',
        timer: 3000,
        showConfirmButton: false,
      })
    } catch (error) {
      console.error('Erro ao atualizar lista de ícones:', error)
      setLoadingIcons(false)

      // Fallback: adiciona apenas o novo ícone localmente
      const updatedIcons = [...icones, newIcon]
      setIcones(updatedIcons)

      // Aplicar filtros novamente
      const filtered = updatedIcons.filter((icon) => {
        if (parseInt(categoryIconsSelect) !== -1) {
          return icon.category === categoryIconsSelect ? true : false
        }
        return true
      })
      setIconesFiltered(filtered)

      // Selecionar automaticamente o novo ícone
      setIconId(newIcon.id)
      setIcon(newIcon.svg)

      Swal.fire({
        icon: 'warning',
        title: 'Ícone adicionado!',
        text: 'O ícone foi adicionado, mas houve um problema ao atualizar a lista completa.',
        timer: 4000,
        showConfirmButton: false,
      })
    }
  }

  const handleIconUpdated = async (updatedIcon: any) => {
    try {
      // Fazer a requisição PUT para atualizar o ícone
      await PUT(`/svgwebapp/${updatedIcon.id}`, {
        description: updatedIcon.name, // Mapeia name para description
        svg: updatedIcon.svg_content, // Mapeia svg_content para svg
      })

      // Atualiza o ícone na lista local
      const updatedIcons = icones.map((icon) =>
        icon.id === updatedIcon.id
          ? {...icon, description: updatedIcon.name, svg: updatedIcon.svg_content}
          : icon
      )
      setIcones(updatedIcons)

      // Reaplica os filtros
      changeFilterIcons()

      Swal.fire({
        title: 'Sucesso!',
        text: 'Ícone atualizado com sucesso.',
        icon: 'success',
        confirmButtonText: 'OK',
      })
    } catch (error) {
      console.error('Erro ao atualizar ícone:', error)
      Swal.fire({
        title: 'Erro!',
        text: 'Erro ao atualizar ícone.',
        icon: 'error',
        confirmButtonText: 'OK',
      })
      throw error // Re-throw para o modal tratar
    }
  }

  const openEditModal = (icon: Icon) => {
    setEditingIcon(icon)
    setShowEditModal(true)
  }

  // Funções para seleção múltipla
  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode)
    setSelectedIcons([]) // Limpa seleções ao alternar modo
  }

  const toggleIconSelection = (iconId: number) => {
    if (selectedIcons.includes(iconId)) {
      setSelectedIcons(selectedIcons.filter((id) => id !== iconId))
    } else {
      setSelectedIcons([...selectedIcons, iconId])
    }
  }

  const selectAllIcons = () => {
    const allVisibleIds = iconesFiltered.map((icon) => icon.id)
    setSelectedIcons(allVisibleIds)
  }

  const deselectAllIcons = () => {
    setSelectedIcons([])
  }

  const handleBulkDelete = async () => {
    if (selectedIcons.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção!',
        text: 'Selecione pelo menos um ícone para excluir.',
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Confirmar Exclusão',
      text: `Deseja excluir ${selectedIcons.length} ícone(s) selecionado(s)?`,
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
    })

    if (result.isConfirmed) {
      try {
        const deletePromises = selectedIcons.map((iconId) => DELETE(`/svgwebapp/${iconId}`))

        const responses = await Promise.all(deletePromises)

        // Verificar se todas as exclusões foram bem-sucedidas
        const successfulDeletes = responses.filter(
          (response) => response && response.statusCode === 200 && response.data?.success
        )

        if (successfulDeletes.length === selectedIcons.length) {
          // Remover ícones excluídos da lista local
          const updatedIcons = icones.filter((icon) => !selectedIcons.includes(icon.id))
          setIcones(updatedIcons)

          // Se algum ícone excluído estava selecionado, limpar seleção
          if (selectedIcons.includes(iconId)) {
            setIconId(0)
            setIcon('')
          }

          // Reaplica os filtros
          changeFilterIcons()

          // Limpar seleções e sair do modo de seleção múltipla
          setSelectedIcons([])
          setIsMultiSelectMode(false)

          Swal.fire({
            icon: 'success',
            title: 'Sucesso!',
            text: `${successfulDeletes.length} ícone(s) excluído(s) com sucesso.`,
            timer: 2000,
            showConfirmButton: false,
          })
        } else {
          throw new Error(
            `Apenas ${successfulDeletes.length} de ${selectedIcons.length} ícones foram excluídos`
          )
        }
      } catch (error: any) {
        console.error('Erro ao excluir ícones:', error)

        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: 'Erro ao excluir alguns ícones. Tente novamente.',
        })
      }
    }
  }

  const getClassifications = async () => {
    try {
      setLoading(true)
      const res = await GET('/service-classification')

      const classifications = [
        {
          value: 0,
          select: i18n.t('service_create.form.classification_placeholder'),
        },
      ]

      if (res.success && res.data) {
        res.data.forEach((row: any) => {
          classifications.push({
            value: row.id,
            select: row.name,
          })
        })
      }

      setServiceClassifications(classifications)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const create = async () => {
    try {
      // Debug dos campos obrigatórios
      console.log('Validação dos campos:')
      console.log('serviceClassificationId.value:', serviceClassificationId.value)
      console.log('name:', name)
      console.log('iconId:', iconId)
      console.log('role:', role)

      if (serviceClassificationId.value === 0 || !name || iconId === 0 || !role) {
        // Identificar qual campo está faltando
        let missingFields = []
        if (serviceClassificationId.value === 0) missingFields.push('Classificação do Serviço')
        if (!name) missingFields.push('Nome do Serviço')
        if (iconId === 0) missingFields.push('Ícone do Serviço')
        if (!role) missingFields.push('Role do Serviço')

        console.log('Campos faltantes:', missingFields)

        Swal.fire({
          icon: 'error',
          title: 'Campos faltantes',
          text: `Verifique se preencheu todos os campos obrigatórios: ${missingFields.join(', ')}`,
        })
      } else {
        setLoading(true)

        const serviceData = {
          serviceClassificationId: serviceClassificationId.value,
          name,
          code: code || '0',
          iconId,
          description,
          time: time || null,
          ramal: ramal || null,
          externalLink: externalLink || null,
          status,
          order,
          priority,
          role,
          visibleTo,
        }

        const res = await POST('/hotel-service', serviceData)

        setLoading(false)
        if (res.success === true) {
          Swal.fire({
            icon: 'success',
            title: String(i18n.t('service_create.success.title')),
            text: String(i18n.t('service_create.success.text')),
          }).then(() => navigate('/hospitality/service-item'))
        } else {
          Swal.fire({
            icon: 'error',
            title: String(i18n.t('service_create.error.title')),
            text: String(res.message || i18n.t('service_create.error.text')),
          })
        }
      }
    } catch (e: any) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('service_create.error.title')),
        text: String(e?.message || i18n.t('service_create.error.text')),
      })
    }
  }

  async function getIcones() {
    try {
      const svgCategory = await POST('svgcategory/filter', {
        status: true,
      })

      const auxCategory = [
        {
          select: i18n.t('service_create.icons.all_categories'),
          value: -1,
        },
      ]

      svgCategory.data.forEach((el: any) => {
        auxCategory.push({
          select: el.name,
          value: el.id,
        })
      })

      setCategoryIcons(auxCategory)

      const res = await GET('svgwebapp')

      if (!res || !res.success) {
        Swal.fire({
          icon: 'error',
          titleText: String(i18n.t('service_create.error.title')),
          text: String(i18n.t('service_create.error.icons_load')),
        })

        return
      }

      // Validação e separação de ícones válidos e inválidos
      const validIcons: Icon[] = []
      const invalidIcons: Icon[] = []

      res.data.forEach((icon: Icon) => {
        const isValidSvg =
          icon.svg &&
          (icon.svg.includes('<svg') ||
            icon.svg.startsWith('data:image/svg+xml') ||
            icon.svg.endsWith('.svg'))

        if (isValidSvg) {
          validIcons.push(icon)
        } else {
          invalidIcons.push(icon)
        }
      })

      // Exclusão automática de ícones inválidos
      if (invalidIcons.length > 0) {
        console.log(
          `🗑️ Encontrados ${invalidIcons.length} ícones inválidos. Iniciando exclusão automática...`
        )

        const deletePromises = invalidIcons.map(async (icon) => {
          try {
            await DELETE(`svgwebapp/${icon.id}`)
            console.log(`✅ Ícone inválido excluído: ID ${icon.id} - "${icon.description}"`)
            return {success: true, icon}
          } catch (error) {
            console.error(`❌ Erro ao excluir ícone ID ${icon.id}:`, error)
            return {success: false, icon}
          }
        })

        const deleteResults = await Promise.all(deletePromises)
        const deletedCount = deleteResults.filter((result) => result.success).length

        if (deletedCount > 0) {
          console.log(`🎯 ${deletedCount} ícones inválidos foram removidos automaticamente`)

          // Exibir notificação discreta para o usuário
          const toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true,
            didOpen: (toast) => {
              toast.addEventListener('mouseenter', Swal.stopTimer)
              toast.addEventListener('mouseleave', Swal.resumeTimer)
            },
          })

          toast.fire({
            icon: 'info',
            title: `${deletedCount} ícones inválidos foram removidos automaticamente`,
          })
        }
      }

      setIcones(validIcons)
      setIconesFiltered(validIcons)
    } catch (error) {
      console.log(error)

      Swal.fire({
        icon: 'error',
        titleText: String(i18n.t('service_create.error.title')),
        text: String(i18n.t('service_create.error.icons_load')),
      })
    }
  }

  const loadData = useCallback(async () => {
    setLoading(true)
    await Promise.all([getClassifications(), getIcones()])
    setLoading(false)
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    changeFilterIcons()
  }, [changeFilterIcons])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <>
            <div className='d-flex justify-content-between'>
              <div className='w-50 me-5'>
                <div className='card pb-5'>
                  <div className='card-header py-5'>
                    <h3 className='card-title align-items-start flex-column'>
                      <span className='card-label fw-bold fs-4 mb-1'>
                        {i18n.t('service_create.page_title')}
                      </span>
                      <BreadCrumbs />
                    </h3>
                  </div>
                  <div className='px-10 py-5'>
                    <Input
                      inputAttr={{
                        type: 'select',
                      }}
                      search
                      placeholder={i18n.t('service_create.form.classification_placeholder')}
                      label={i18n.t(`smart_order.category_parent`)}
                      options={serviceClassifications}
                      value={serviceClassificationId}
                      change={(value: any) => setServiceClassificationId(value)}
                    />

                    <Input
                      inputAttr={{
                        type: 'default',
                      }}
                      placeholder={i18n.t('service_create.form.name_placeholder')}
                      label={i18n.t('service_create.form.name_label')}
                      value={name}
                      change={(value: string) => setName(value)}
                    />

                    <EditorQuill
                      value={description}
                      placeholder={i18n.t('service_create.form.description_placeholder')}
                      label={i18n.t('service_create.form.description_label')}
                      change={(value: string) => setDescription(value)}
                      maxLength={1000} // Limite de 5000 caracteres
                    />

                    <div className='row'>
                      <div className='col-md-6'>
                        <Input
                          inputAttr={{
                            type: 'default',
                          }}
                          placeholder={i18n.t('service_create.form.schedule_placeholder')}
                          label={i18n.t('service_create.form.schedule_label')}
                          value={time}
                          change={(value: string) => setTime(value)}
                          smalltext={i18n.t('service_create.form.schedule_smalltext')}
                          maxCaracteres={50}
                        />
                      </div>
                      <div className='col-md-6'>
                        <Input
                          inputAttr={{
                            type: 'default',
                          }}
                          placeholder={i18n.t('service_create.form.extension_placeholder')}
                          label={i18n.t('service_create.form.extension_label')}
                          value={ramal}
                          change={(value: string) => setRamal(value)}
                          smalltext={i18n.t('service_create.form.extension_smalltext')}
                          maxCaracteres={10}
                        />
                      </div>
                    </div>

                    <div className='row'>
                      <div className='col-md-6'>
                        <Input
                          inputAttr={{
                            type: 'default',
                          }}
                          placeholder={i18n.t('service_create.form.code_placeholder')}
                          label={i18n.t('service_create.form.code_label')}
                          value={code}
                          change={(value: string) => setCode(value)}
                        />
                      </div>
                      <div className='col-md-6'>
                        <Input
                          inputAttr={{
                            type: 'number',
                          }}
                          placeholder={i18n.t('service_create.form.order_placeholder')}
                          label={i18n.t('service_create.form.order_label')}
                          value={order.toString()}
                          minNumber={0}
                          change={(value: string) => setOrder(parseInt(value) || 0)}
                        />
                      </div>
                    </div>

                    <Input
                      inputAttr={{
                        type: 'select',
                      }}
                      label={i18n.t('service_create.form.status_label')}
                      options={[
                        {select: i18n.t('service_create.form.status_active'), value: true},
                        {select: i18n.t('service_create.form.status_inactive'), value: false},
                      ]}
                      value={status}
                      change={(value: boolean) => setStatus(value)}
                    />

                    <Input
                      inputAttr={{
                        type: 'select',
                      }}
                      label={i18n.t('service_create.form.priority_label')}
                      options={[
                        {select: i18n.t('service_create.form.priority_low'), value: 'low'},
                        {select: i18n.t('service_create.form.priority_normal'), value: 'normal'},
                        {select: i18n.t('service_create.form.priority_high'), value: 'high'},
                        {
                          select: i18n.t('service_create.form.priority_critical'),
                          value: 'critical',
                        },
                      ]}
                      value={priority}
                      change={(value: string) => setPriority(value)}
                    />

                    <Input
                      inputAttr={{
                        type: 'select',
                      }}
                      label='Role do Serviço'
                      placeholder='Selecione o role'
                      options={[{select: 'Nenhum', value: ''}, ...typeUser('hospitality')]}
                      value={role}
                      change={(value: string) => setRole(value)}
                    />

                    <Input
                      inputAttr={{
                        type: 'select',
                      }}
                      label={i18n.t('service_requests.visible_to_label')}
                      placeholder='Selecione a visibilidade'
                      options={[
                        {
                          select: i18n.t('service_requests.visible_to_employees'),
                          value: 'employees',
                        },
                        {
                          select: i18n.t('service_requests.visible_to_customers'),
                          value: 'customers',
                        },
                        {select: i18n.t('service_requests.visible_to_both'), value: 'both'},
                      ]}
                      value={visibleTo}
                      change={(value: string) => setVisibleTo(value)}
                    />

                    <Input
                      inputAttr={{
                        type: 'default',
                      }}
                      label={i18n.t('service_create.form.external_link_label')}
                      placeholder={i18n.t('service_create.form.external_link_placeholder')}
                      value={externalLink}
                      change={(value: string) => setExternalLink(value)}
                      maxCaracteres={250}
                    />
                  </div>
                  <div className='d-flex justify-content-between px-10 py-5'>
                    <Button
                      text={i18n.t('buttons.back')}
                      color='info'
                      size='small'
                      horizontalPadding={5}
                      btnAttr={{
                        type: 'button',
                      }}
                      click={() => {
                        navigate('/hospitality/service-item')
                      }}
                      btnClass='fw-bolder me-3'
                    />
                    <Button
                      text={i18n.t('buttons.save')}
                      color='primary'
                      size='small'
                      horizontalPadding={5}
                      btnAttr={{
                        type: 'button',
                      }}
                      click={() => {
                        create()
                      }}
                      btnClass='fw-bolder'
                    />
                  </div>
                </div>
              </div>
              <div className='w-50 card h-100'>
                <div className='card-header py-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold fs-4 mb-1'>
                      {i18n.t('service_create.icons.title')}
                    </span>
                  </h3>
                </div>

                <div
                  className='d-flex flex-column overflow-auto  gap-5 px-8 py-5'
                  style={{
                    height: '55rem',
                  }}
                >
                  <div className='d-flex justify-content-between align-items-end'>
                    <div style={{flex: 1, marginRight: '1rem'}}>
                      <Input
                        inputAttr={{
                          type: 'select',
                        }}
                        label={i18n.t('service_create.icons.category_label')}
                        options={categoryIcons}
                        defaultValue={categoryIcons[0]}
                        value={categoryIconsSelect || ''}
                        change={(value: any) => {
                          setCategoryIconsSelect(value)
                        }}
                      />
                    </div>
                    {isUserAdmin() && (
                      <div>
                        <Button
                          text='+ Upload Ícone'
                          color='success'
                          size='small'
                          horizontalPadding={3}
                          btnAttr={{
                            type: 'button',
                          }}
                          click={() => setShowUploadModal(true)}
                          btnClass='fw-bolder'
                        />
                      </div>
                    )}
                  </div>

                  {/* Campo de busca de ícones */}
                  <div className='mb-4'>
                    <Input
                      inputAttr={{
                        type: 'text',
                        placeholder: 'Buscar ícones por descrição...',
                      }}
                      label='Buscar Ícones'
                      value={searchIconText}
                      change={(value: string) => setSearchIconText(value)}
                    />
                  </div>

                  {/* Controles de seleção múltipla */}
                  {isUserAdmin() && (
                    <div className='d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded'>
                      <div className='d-flex align-items-center gap-3'>
                        <Button
                          text={isMultiSelectMode ? 'Cancelar Seleção' : 'Selecionar Múltiplos'}
                          color={isMultiSelectMode ? 'secondary' : 'info'}
                          size='small'
                          click={toggleMultiSelectMode}
                          btnClass='fw-bolder'
                        />

                        {isMultiSelectMode && (
                          <>
                            <Button
                              text='Selecionar Todos'
                              color='light'
                              size='small'
                              click={selectAllIcons}
                              btnClass='fw-bolder'
                            />
                            <Button
                              text='Desmarcar Todos'
                              color='light'
                              size='small'
                              click={deselectAllIcons}
                              btnClass='fw-bolder'
                            />
                          </>
                        )}
                      </div>

                      {isMultiSelectMode && selectedIcons.length > 0 && (
                        <div className='d-flex align-items-center gap-3'>
                          <span className='text-muted'>
                            {selectedIcons.length} ícone(s) selecionado(s)
                          </span>
                          <Button
                            text='Excluir Selecionados'
                            color='danger'
                            size='small'
                            click={handleBulkDelete}
                            btnClass='fw-bolder'
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className=' d-flex overflow-auto flex-wrap gap-5 justify-content-center'>
                    {loadingIcons ? (
                      <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
                        <Loading />
                      </div>
                    ) : (
                      <>
                        {iconesFiltered.map((iconF) => (
                          <div
                            key={iconF.id}
                            className={`position-relative d-flex flex-column p-4 justify-content-center align-items-center ${
                              isMultiSelectMode && selectedIcons.includes(iconF.id)
                                ? 'border border-warning border-2 bg-warning bg-opacity-10'
                                : iconF.id === iconId
                                ? 'border border-success border-2'
                                : 'border border-back border-2'
                            }`}
                            style={{
                              width: '20%',
                              cursor: 'pointer',
                            }}
                          >
                            {/* Checkbox para seleção múltipla */}
                            {isMultiSelectMode && (
                              <input
                                type='checkbox'
                                className='form-check-input position-absolute'
                                style={{
                                  top: '5px',
                                  left: '5px',
                                  width: '20px',
                                  height: '20px',
                                  zIndex: 10,
                                }}
                                checked={selectedIcons.includes(iconF.id)}
                                onChange={() => toggleIconSelection(iconF.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}

                            {/* Botão de Editar no canto superior direito */}
                            {!isMultiSelectMode && isUserAdmin() && (
                              <button
                                className='btn btn-sm btn-primary position-absolute'
                                style={{
                                  top: '5px',
                                  right: '5px',
                                  width: '25px',
                                  height: '25px',
                                  padding: '0',
                                  borderRadius: '50%',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 10,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation() // Impede que o clique selecione o ícone
                                  openEditModal(iconF)
                                }}
                                title='Editar ícone'
                              >
                                ✏️
                              </button>
                            )}

                            {/* Área clicável para seleção do ícone */}
                            <div
                              className='w-100 h-100 d-flex flex-column justify-content-center align-items-center'
                              onClick={() => {
                                if (isMultiSelectMode) {
                                  toggleIconSelection(iconF.id)
                                } else {
                                  setIconId(iconF.id)
                                  setIcon(iconF.svg)
                                }
                              }}
                            >
                              <span
                                className='mb-2 text-center'
                                style={{fontSize: '0.8rem', wordBreak: 'break-word'}}
                              >
                                {iconF.description}
                              </span>

                              {/* Renderização condicional do ícone */}
                              {iconF.svg &&
                                (iconF.svg.includes('<svg') ? (
                                  // SVG inline
                                  <div
                                    dangerouslySetInnerHTML={{__html: iconF.svg}}
                                    style={{
                                      width: '45px',
                                      height: '45px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  />
                                ) : iconF.svg.startsWith('data:image/svg+xml') ||
                                  iconF.svg.endsWith('.svg') ? (
                                  // SVG como URL ou data URL
                                  <SVG
                                    src={iconF.svg}
                                    style={{
                                      width: '45px',
                                      height: '45px',
                                    }}
                                    onError={() => {
                                      console.warn(`Erro ao carregar SVG: ${iconF.description}`)
                                    }}
                                  />
                                ) : (
                                  // Fallback para ícones inválidos
                                  <div
                                    style={{
                                      width: '45px',
                                      height: '45px',
                                      border: '2px dashed #ccc',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.7rem',
                                      color: '#999',
                                      textAlign: 'center',
                                    }}
                                  >
                                    SVG inválido
                                  </div>
                                ))}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  <div className='d-flex justify-content-between gap-5 border-top border-black'>
                    <div className='d-flex flex-column justify-content-start gap-5 mt-5'>
                      <span className='fs-3 fw-bold'>
                        {i18n.t('service_create.icons.selected_icon')}
                      </span>
                      {icon && (
                        <div className='px-8 py-5 d-flex justify-content-center'>
                          <SVG
                            src={icon}
                            style={{
                              width: '45px',
                              height: '45px',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        </div>
      )}

      {/* Modal de Upload de SVG */}
      <SVGUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onIconUploaded={handleIconUploaded}
        categories={categoryIcons}
      />

      {/* Modal de Edição de Ícone */}
      <IconEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        icon={editingIcon}
        onSave={handleIconUpdated}
      />
    </>
  )
}

const CriarServico: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {
            title: i18n.t('service_create.breadcrumb.services'),
            path: 'service-item',
            isActive: true,
          },
          {title: i18n.t('service_create.breadcrumb.create'), path: '#', isActive: true},
        ]}
      >
        {i18n.t('service_create.page_title')}
      </PageTitle>
      <PageContent />
    </>
  )
}

export {CriarServico}
